import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import HouseBars, { HouseSwatch } from '../../components/common/HouseBars';
import CompanyEventRow from './CompanyEventRow';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './MyCompetition.css';

/* The competition an employee of an engagement customer is IN — their house, the standing, their
   teams, and every day the company has played or will play.

   ★ The employee's screen, not a looser version of the admin's. It reads
   GET /api/company/my-competition, which sits behind the EMPLOYEE gate and answers with the
   standing and nothing to post back — see api/routes/company_standings.py for why that is a
   separate route rather than the admin's with a wider door.

   ★★ Every number on this page came out of the server already totalled. Nothing here adds points
   up. A house total is `company_house_points` (which is `fetch_house_scores` per day, summed) and
   a team's is `fetch_teams_with_scores` — the same queries the organiser and the beamer read. A
   second sum in the browser would be a second answer to "who is winning".

   ⚠️ THE FAN-OUT IS SAID OUT LOUD under the bars, and that line is not decoration. A house total
   is the plain sum of its TEAMS' totals, so 50 points awarded to a house of six teams reads as
   300. Somebody who reads a big number with no explanation and then counts the awards by hand
   concludes the screen is broken.

   ★ The day list is EVERY competition day of the company and the chips narrow it, which is what
   the person who asked for this screen asked for. Filtered in the browser — the page already
   holds the list — and the counts are tallied over the WHOLE list so they do not move while you
   click them. That is the same rule LabFilters and the company calendar both hold. */

// The two filter axes. Kept as constants because the chip, the match and the count all have to
// agree on the same word; three string literals is three chances for them not to.
const WHO_ALL = 'all';
const WHO_MINE = 'mine';
const WHEN_ALL = 'all';
const WHEN_UPCOMING = 'upcoming';
const WHEN_PAST = 'past';

const fmt = (n) => Number(n || 0).toLocaleString();

const Chip = ({ on, n, label, onClick }) => (
  <button type="button" className={`ce-tab${on ? ' is-on' : ''}`} onClick={onClick}>
    {label}
    {n != null && <span className="ce-tab__n">{n}</span>}
  </button>
);

const MyCompetition = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [who, setWho] = useState(WHO_ALL);
  const [when, setWhen] = useState(WHEN_ALL);

  const load = useCallback(() => {
    setError(null);
    apiRequest('GET', '/company/my-competition')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => (data && data.days) || [], [data]);

  // ★ Tallied over the whole list, never over the filtered one. A count that shrank as you
  // clicked would read as "there are fewer of these now", when all that happened is that you
  // asked to see some of them.
  const counts = useMemo(() => ({
    all: days.length,
    mine: days.filter((d) => d.is_mine).length,
    upcoming: days.filter((d) => !d.is_past).length,
    past: days.filter((d) => d.is_past).length,
  }), [days]);

  const shown = useMemo(() => days.filter((d) => {
    if (who === WHO_MINE && !d.is_mine) return false;
    if (when === WHEN_UPCOMING && d.is_past) return false;
    if (when === WHEN_PAST && !d.is_past) return false;
    return true;
  }), [days, who, when]);

  /* The platform-wide way to act on a team that is not the one on your token: stash the id, then
     navigate. Every team screen reads it back and sends it as the `Otherteamid` header. */
  const openTeam = (teamId, path) => {
    sessionStorage.setItem('otherTeamId', teamId);
    navigate(path);
  };

  if (error) {
    return (
      <Base activeNav="home">
        <Card><p className="bravos-cal__notice">{error}</p></Card>
      </Base>
    );
  }
  if (!data) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const houses = data.houses || [];
  const teams = data.teams || [];
  const mine = data.house;

  return (
    <Base activeNav="home">
      <Card>
        <Title my="1" className="mt-2">
          <Icon name="trophy" theme="primary" className="me-2" />
          {t('myCompetition')}
        </Title>

        {/* Which house they are OF. Membership is standing; which house their TEAM competed for
            on a given day is a different fact, and the day's own board is where that is said. */}
        {mine && (
          <div className="mycomp-hero">
            <HouseSwatch house={mine} size="lg" />
            <div className="mycomp-hero__body">
              <span className="mycomp-me__label">{t('myHouse')}</span>
              <span className="mycomp-hero__name">{mine.name}</span>
              <span className="mycomp-me__sub">
                {t('housePlaceOf', { rank: mine.rank, total: houses.length })}
                {' · '}
                {t('nPoints', { n: fmt(mine.points) })}
              </span>
            </div>
          </div>
        )}

        {houses.length > 0 ? (
          <>
            <p className="bravos-cal__label">{t('houseStandingTitle')}</p>
            <HouseBars rows={houses} mineId={mine && mine.id} />

            {/* The same sentences the admin's house stats print, word for word and key for key,
                and picked by the same rule — the payload says through `basis` WHICH standing the
                bars above are, and the note has to match it. With the cup, the first sentence is
                what the number means; without it, the first is why the number is as big as it
                is and the second admits the figure is a tail. Either way, an unexplained total
                reads as a bug. */}
            {data.basis === 'cup' ? (
              <p className="ce-note mt-2">{t('statsCupNote')}</p>
            ) : (
              <>
                {data.days_played === 0
                  ? <p className="ce-note mt-2">{t('statsHousesNoDays')}</p>
                  : <p className="ce-note mt-2">{t('statsHousePointsNote')}</p>}
                {data.points_capped && <p className="ce-note">{t('statsHousePointsCapped')}</p>}
              </>
            )}
          </>
        ) : (
          <p className="ce-note">{t('statsNoHousesYet')}</p>
        )}
      </Card>

      {/* Who else is in my house. Faces rather than a list of names: the reason to look is to
          recognise somebody, and a name you do not place tells you nothing. */}
      {mine && (mine.members || []).length > 0 && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="users" theme="primary" className="me-2" />
            {t('myHousemates')}
          </Title>
          <p className="bravos-q__hint mb-2">
            {t('statsNMembers', { n: (mine.members || []).length })}
          </p>
          <div className="mycomp-faces">
            {(mine.members || []).map((m) => (
              <span key={m.member_id || m.person_id} className="mycomp-face" title={m.name}>
                <span
                  className="mycomp-face__img"
                  style={m.photo_url ? { backgroundImage: `url("${m.photo_url}")` } : {}}
                >
                  {!m.photo_url && (m.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="mycomp-face__name">{m.name}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* My teams. One row each, with the two things a player wants from a team: look at it, and
          change it. "Edit" is the door that did not exist — /edit-team fell back to the token's
          team, which for a Bravos member is not this one. */}
      {teams.length > 0 && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="users-alt" theme="primary" className="me-2" />
            {t('myTeams')}
          </Title>
          {teams.map((team) => (
            <div key={team.team_id} className="mycomp-teamrow">
              <span
                className="mycomp-teamrow__media"
                style={team.photo_url ? { backgroundImage: `url("${team.photo_url}")` } : {}}
              />
              <span className="mycomp-teamrow__body">
                <span className="mycomp-teamrow__name">
                  {team.name}
                  {team.house_name && (
                    <span className="mycomp-teamrow__house"
                          style={{ backgroundColor: team.house_color || '#5a6472' }}>
                      {team.house_name}
                    </span>
                  )}
                </span>
                <span className="mycomp-teamrow__meta">{team.event_name}</span>
                <span className="mycomp-teamrow__stats">
                  {/* ★ No rank while the board is closed, and the reason SAID rather than left
                      as a gap: nothing is revealed yet, so every team is level on zero and a
                      position would be invented. The server decides this — see _board_state. */}
                  {team.board_state === 'closed' ? (
                    <span className="mycomp-teamrow__closed">{t('boardNotOpenYet')}</span>
                  ) : (
                    <>
                      {team.rank != null && (
                        <span className="mycomp-teamrow__rank">
                          {t('rankOfN', { rank: team.rank, total: team.total_teams })}
                        </span>
                      )}
                      <span>{t('nPoints', { n: fmt(team.points) })}</span>
                    </>
                  )}
                  {/* Their OWN points, beside the team's. `own + via_team === team_total` on the
                      server, so these two never double-count each other. */}
                  {team.my_points > 0 && (
                    <span className="mycomp-teamrow__own">
                      {t('nPointsByYou', { n: fmt(team.my_points) })}
                    </span>
                  )}
                </span>
                <span className="mycomp-teamrow__actions">
                  <button type="button" className="ce-tab"
                          onClick={() => navigate(`/public-team/${team.team_id}`)}>
                    {t('view')}
                  </button>
                  <button type="button" className="ce-tab"
                          onClick={() => openTeam(team.team_id, '/edit-team')}>
                    <Icon name="pencil" className="me-1" />{t('edit')}
                  </button>
                  <button type="button" className="ce-tab"
                          onClick={() => navigate(`/company-event/${team.event_id}/standings`)}>
                    <Icon name="leaderboard" className="me-1" />{t('standings')}
                  </button>
                </span>
              </span>
            </div>
          ))}
          {data.teams_capped && <p className="ce-note">{t('myTeamsCapped')}</p>}
        </Card>
      )}

      {/* Every competition day the company runs, with the chips that narrow it. */}
      <Card>
        <Title my="1" className="mt-2">
          <Icon name="flag-checkered" theme="primary" className="me-2" />
          {t('competitionDays')}
        </Title>

        {days.length > 0 && (
          <>
            <div className="ce-tabs">
              <Chip on={who === WHO_ALL} n={counts.all} label={t('filterAllDays')}
                    onClick={() => setWho(WHO_ALL)} />
              <Chip on={who === WHO_MINE} n={counts.mine} label={t('filterMyDays')}
                    onClick={() => setWho(WHO_MINE)} />
            </div>
            <div className="ce-tabs">
              <Chip on={when === WHEN_ALL} label={t('filterAnyTime')}
                    onClick={() => setWhen(WHEN_ALL)} />
              <Chip on={when === WHEN_UPCOMING} n={counts.upcoming} label={t('filterUpcoming')}
                    onClick={() => setWhen(WHEN_UPCOMING)} />
              <Chip on={when === WHEN_PAST} n={counts.past} label={t('filterPast')}
                    onClick={() => setWhen(WHEN_PAST)} />
            </div>
          </>
        )}

        {shown.length === 0 ? (
          <p className="ce-note">
            {days.length === 0 ? t('noCompetitionDays') : t('noDaysMatchFilter')}
          </p>
        ) : shown.map((day) => (
          <CompanyEventRow
            key={day.id}
            event={day}
            lang={i18n.language}
            thumb
            onOpen={() => navigate(`/public-event/${day.id}`)}
            badges={day.is_mine && (
              <span className="ce-pill ce-pill--competition">{t('youArePlaying')}</span>
            )}
          >
            {/* A day that has BEGUN has a board — today's included, which is when people
                refresh it most. One that has not started yet has nothing to show, so it gets no
                button rather than a button onto an empty table. */}
            {day.has_standings && (
              <button
                type="button"
                className="ce-tab"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/company-event/${day.id}/standings`);
                }}
              >
                <Icon name="leaderboard" className="me-1" />{t('standings')}
              </button>
            )}
          </CompanyEventRow>
        ))}
      </Card>
    </Base>
  );
};

export default MyCompetition;
